import domain from '../../../../reusecomponent/domain';
import { getToken } from '../../../function/token';
import { ErrorToastContainer, SuccessToastContainer } from '../../../function/CustomFunction';

class ChatbotAPI {
  constructor() {
    this.baseURL = domain();
    this.token = getToken();
    console.log('🔧 ChatbotAPI initialized with baseURL:', this.baseURL);
  }

  // Get authorization headers
  getHeaders() {
    const headers = {
      'Authorization': `Bearer ${this.token}`,
      'Content-Type': 'application/json',
    };
    console.log('🔑 Headers prepared:', headers);
    return headers;
  }

  // ==================== CHATBOT CRUD OPERATIONS ====================

  // Get all chatbots for the authenticated user
  async getAllChatbots() {
    console.log('📋 === GETTING ALL CHATBOTS ===');
    try {
      const response = await fetch(`${this.baseURL}/chatbot`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      console.log('🌐 GET /chatbot response status:', response.status);
      const result = await response.json();
      console.log('📦 GET /chatbot response:', result);
      
      if (result.status === true || (result.status === false && result.data && Array.isArray(result.data))) {
        console.log('✅ Successfully retrieved chatbots:', result.data);
        return result.data || [];
      } else {
        console.error('❌ Failed to get chatbots:', result.message);
        ErrorToastContainer(result.message || 'Failed to load chatbots');
        return [];
      }
    } catch (error) {
      console.error('❌ Network error while getting chatbots:', error);
      ErrorToastContainer('Network error while loading chatbots');
      return [];
    }
  }

  // Get a single chatbot by ID
  async getChatbot(id) {
    console.log('🔍 === GETTING CHATBOT BY ID ===');
    console.log('🆔 Chatbot ID:', id);
    
    // Encode ID to Base64 for API request
    const encodedId = this.encodeId(id);
    console.log('🆔 Encoded Chatbot ID:', encodedId);
    
    try {
      const response = await fetch(`${this.baseURL}/chatbot/${encodedId}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      console.log('🌐 GET /chatbot/{id} response status:', response.status);
      const result = await response.json();
      console.log('📦 GET /chatbot/{id} response:', result);
      
      if (result.status === true) {
        console.log('✅ Successfully retrieved chatbot:', result.data);
        return result.data;
      } else {
        console.error('❌ Failed to get chatbot:', result.message);
        ErrorToastContainer(result.message || 'Failed to load chatbot');
        return null;
      }
    } catch (error) {
      console.error('❌ Network error while getting chatbot:', error);
      ErrorToastContainer('Network error while loading chatbot');
      return null;
    }
  }

  // Create a new chatbot
  async createChatbot(chatbotData) {
    console.log('➕ === CREATING NEW CHATBOT ===');
    console.log('📝 Chatbot data to create:', chatbotData);
    
    const requestBody = {
      isactive: chatbotData.isactive || 1,
      chatbot_name: chatbotData.chatbot_name || 'Untitled Chatbot',
      tgm: chatbotData.tgm || [],
      chatbot_node: chatbotData.chatbot_node || []
    };
    
    console.log('📤 Request body for POST /chatbot:', requestBody);
    
    try {
      const response = await fetch(`${this.baseURL}/chatbot`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(requestBody)
      });

      console.log('🌐 POST /chatbot response status:', response.status);
      const result = await response.json();
      console.log('📦 POST /chatbot response:', result);
      
      if (result.status === true) {
        console.log('✅ Successfully created chatbot:', result.data);
        SuccessToastContainer('Chatbot created successfully!');
        return result.data;
      } else {
        console.error('❌ Failed to create chatbot:', result.message);
        ErrorToastContainer(result.message || 'Failed to create chatbot');
        return null;
      }
    } catch (error) {
      console.error('❌ Network error while creating chatbot:', error);
      ErrorToastContainer('Network error while creating chatbot');
      return null;
    }
  }

  // Update an existing chatbot
  async updateChatbot(id, chatbotData) {
    console.log('✏️ === UPDATING CHATBOT ===');
    console.log('🆔 Chatbot ID to update:', id);
    console.log('📝 Chatbot data to update:', chatbotData);
    
    const requestBody = {
      id: id,
      isactive: chatbotData.isactive || 1,
      chatbot_name: chatbotData.chatbot_name || 'Untitled Chatbot',
      tgm: chatbotData.tgm || [],
      chatbot_node: chatbotData.chatbot_node || []
    };
    
    console.log('📤 Request body for POST /chatbot/{id}:', requestBody);
    
    try {
      const response = await fetch(`${this.baseURL}/chatbot/${this.encodeId(id)}`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(requestBody)
      });

      console.log('🌐 POST /chatbot/{id} response status:', response.status);
      const result = await response.json();
      console.log('📦 POST /chatbot/{id} response:', result);
      
      if (result.status === true) {
        console.log('✅ Successfully updated chatbot:', result.data);
        SuccessToastContainer('Chatbot updated successfully!');
        return result.data;
      } else {
        console.error('❌ Failed to update chatbot:', result.message);
        ErrorToastContainer(result.message || 'Failed to update chatbot');
        return null;
      }
    } catch (error) {
      console.error('❌ Network error while updating chatbot:', error);
      ErrorToastContainer('Network error while updating chatbot');
      return null;
    }
  }

  // Delete a chatbot
  async deleteChatbot(id) {
    console.log('🗑️ === DELETING CHATBOT ===');
    console.log('🆔 Chatbot ID to delete:', id);
    
    try {
      const response = await fetch(`${this.baseURL}/chatbot/${this.encodeId(id)}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
      });

      console.log('🌐 DELETE /chatbot/{id} response status:', response.status);
      const result = await response.json();
      console.log('📦 DELETE /chatbot/{id} response:', result);
      
      if (result.status === true) {
        console.log('✅ Successfully deleted chatbot');
        SuccessToastContainer('Chatbot deleted successfully!');
        return true;
      } else {
        console.error('❌ Failed to delete chatbot:', result.message);
        ErrorToastContainer(result.message || 'Failed to delete chatbot');
        return false;
      }
    } catch (error) {
      console.error('❌ Network error while deleting chatbot:', error);
      ErrorToastContainer('Network error while deleting chatbot');
      return false;
    }
  }

  // ==================== FLOW OPERATIONS (LEGACY SUPPORT) ====================

  // Save chatbot flow to backend (legacy method - now uses createChatbot)
  async saveFlow(flowData) {
    console.log('💾 === SAVING FLOW (LEGACY) ===');
    console.log('📝 Flow data:', flowData);
    
    // Use the new conversion function
    const chatbotData = this.convertFlowToChatbot(flowData);
    
    console.log('🔄 Converting flow data to chatbot format:', chatbotData);
    
    if (flowData.id) {
      // Update existing flow
      return await this.updateChatbot(flowData.id, chatbotData);
    } else {
      // Create new flow
      return await this.createChatbot(chatbotData);
    }
  }

  // Load chatbot flows from backend (legacy method - now uses getAllChatbots)
  async loadFlows() {
    console.log('📥 === LOADING FLOWS (LEGACY) ===');
    
    try {
      const chatbots = await this.getAllChatbots();
      return chatbots.map(chatbot => this.convertChatbotToFlow(chatbot));
    } catch (error) {
      console.error('❌ Error loading flows:', error);
      return [];
    }
  }

  // Get specific flow (legacy method - now uses getChatbot)
  async getFlow(flowId) {
    console.log('📥 === GETTING FLOW (LEGACY) ===');
    console.log('🆔 Flow ID:', flowId);
    
    try {
      const chatbot = await this.getChatbot(flowId);
      if (chatbot) {
        return this.convertChatbotToFlow(chatbot);
      }
      return null;
    } catch (error) {
      console.error('❌ Error getting flow:', error);
      return null;
    }
  }

  // Update existing flow (legacy method - now uses updateChatbot)
  async updateFlow(flowId, flowData) {
    console.log('✏️ === UPDATING FLOW (LEGACY) ===');
    console.log('🆔 Flow ID:', flowId);
    console.log('📝 Flow data:', flowData);
    
    // Use the new conversion function
    const chatbotData = this.convertFlowToChatbot(flowData);
    
    console.log('🔄 Converting flow data to chatbot format:', chatbotData);
    return await this.updateChatbot(flowId, chatbotData);
  }

  // Delete flow (legacy method - now uses deleteChatbot)
  async deleteFlow(flowId) {
    console.log('🗑️ === DELETING FLOW (LEGACY) ===');
    console.log('🆔 Flow ID:', flowId);
    return await this.deleteChatbot(flowId);
  }

  // ==================== CONVERSATION OPERATIONS ====================

  // Start a conversation with a flow
  async startConversation(flowId, customerData) {
    console.log('💬 === STARTING CONVERSATION ===');
    console.log('🆔 Flow ID:', flowId);
    console.log('👤 Customer data:', customerData);
    
    try {
      const response = await fetch(`${this.baseURL}/chatbot/conversations`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          flow_id: flowId,
          customer_phone: customerData.phone,
          customer_name: customerData.name,
          customer_email: customerData.email,
          initial_message: customerData.initialMessage
        })
      });

      console.log('🌐 POST /chatbot/conversations response status:', response.status);
      const result = await response.json();
      console.log('📦 POST /chatbot/conversations response:', result);
      
      if (result.status === true) {
        console.log('✅ Successfully started conversation:', result.data);
        return result.data;
      } else {
        console.error('❌ Failed to start conversation:', result.message);
        ErrorToastContainer(result.message || 'Failed to start conversation');
        return null;
      }
    } catch (error) {
      console.error('❌ Network error while starting conversation:', error);
      ErrorToastContainer('Network error while starting conversation');
      return null;
    }
  }

  // Send message in conversation
  async sendMessage(conversationId, message, nodeId = null) {
    console.log('💬 === SENDING MESSAGE ===');
    console.log('🆔 Conversation ID:', conversationId);
    console.log('📝 Message:', message);
    console.log('🆔 Node ID:', nodeId);
    
    try {
      const response = await fetch(`${this.baseURL}/chatbot/conversations/${conversationId}/messages`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          message: message,
          node_id: nodeId,
          timestamp: new Date().toISOString()
        })
      });

      console.log('🌐 POST /chatbot/conversations/{id}/messages response status:', response.status);
      const result = await response.json();
      console.log('📦 POST /chatbot/conversations/{id}/messages response:', result);
      
      if (result.status === true) {
        console.log('✅ Successfully sent message:', result.data);
        return result.data;
      } else {
        console.error('❌ Failed to send message:', result.message);
        ErrorToastContainer(result.message || 'Failed to send message');
        return null;
      }
    } catch (error) {
      console.error('❌ Network error while sending message:', error);
      ErrorToastContainer('Network error while sending message');
      return null;
    }
  }

  // Get conversation history
  async getConversationHistory(conversationId) {
    console.log('📚 === GETTING CONVERSATION HISTORY ===');
    console.log('🆔 Conversation ID:', conversationId);
    
    try {
      const response = await fetch(`${this.baseURL}/chatbot/conversations/${conversationId}/messages`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      console.log('🌐 GET /chatbot/conversations/{id}/messages response status:', response.status);
      const result = await response.json();
      console.log('📦 GET /chatbot/conversations/{id}/messages response:', result);
      
      if (result.status === true) {
        console.log('✅ Successfully retrieved conversation history:', result.data);
        return result.data || [];
      } else {
        console.error('❌ Failed to load conversation history:', result.message);
        ErrorToastContainer(result.message || 'Failed to load conversation history');
        return [];
      }
    } catch (error) {
      console.error('❌ Network error while loading conversation history:', error);
      ErrorToastContainer('Network error while loading conversation history');
      return [];
    }
  }

  // Get chatbot analytics
  async getAnalytics(flowId = null, dateRange = null) {
    console.log('📊 === GETTING ANALYTICS ===');
    console.log('🆔 Flow ID:', flowId);
    console.log('📅 Date range:', dateRange);
    
    try {
      let url = `${this.baseURL}/chatbot/analytics`;
      const params = new URLSearchParams();
      
      if (flowId) params.append('flow_id', flowId);
      if (dateRange) {
        params.append('start_date', dateRange.start);
        params.append('end_date', dateRange.end);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      console.log('🌐 GET URL:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      console.log('🌐 GET /chatbot/analytics response status:', response.status);
      const result = await response.json();
      console.log('📦 GET /chatbot/analytics response:', result);
      
      if (result.status === true) {
        console.log('✅ Successfully retrieved analytics:', result.data);
        return result.data;
      } else {
        console.error('❌ Failed to load analytics:', result.message);
        ErrorToastContainer(result.message || 'Failed to load analytics');
        return null;
      }
    } catch (error) {
      console.error('❌ Network error while loading analytics:', error);
      ErrorToastContainer('Network error while loading analytics');
      return null;
    }
  }

  // Get active conversations
  async getActiveConversations() {
    console.log('💬 === GETTING ACTIVE CONVERSATIONS ===');
    
    try {
      const response = await fetch(`${this.baseURL}/chatbot/conversations/active`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      console.log('🌐 GET /chatbot/conversations/active response status:', response.status);
      const result = await response.json();
      console.log('📦 GET /chatbot/conversations/active response:', result);
      
      if (result.status === true) {
        console.log('✅ Successfully retrieved active conversations:', result.data);
        return result.data || [];
      } else {
        console.error('❌ Failed to load active conversations:', result.message);
        ErrorToastContainer(result.message || 'Failed to load active conversations');
        return [];
      }
    } catch (error) {
      console.error('❌ Network error while loading active conversations:', error);
      ErrorToastContainer('Network error while loading active conversations');
      return [];
    }
  }

  // End conversation
  async endConversation(conversationId, reason = 'completed') {
    console.log('🔚 === ENDING CONVERSATION ===');
    console.log('🆔 Conversation ID:', conversationId);
    console.log('📝 Reason:', reason);
    
    try {
      const response = await fetch(`${this.baseURL}/chatbot/conversations/${conversationId}/end`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          reason: reason,
          ended_at: new Date().toISOString()
        })
      });

      console.log('🌐 POST /chatbot/conversations/{id}/end response status:', response.status);
      const result = await response.json();
      console.log('📦 POST /chatbot/conversations/{id}/end response:', result);
      
      if (result.status === true) {
        console.log('✅ Successfully ended conversation');
        SuccessToastContainer('Conversation ended successfully!');
        return true;
      } else {
        console.error('❌ Failed to end conversation:', result.message);
        ErrorToastContainer(result.message || 'Failed to end conversation');
        return false;
      }
    } catch (error) {
      console.error('❌ Network error while ending conversation:', error);
      ErrorToastContainer('Network error while ending conversation');
      return false;
    }
  }

  // ==================== UTILITY METHODS ====================

  // Encode ID to Base64 (for API requests)
  encodeId(id) {
    return btoa(id.toString());
  }

  // Decode Base64 ID (for API responses)
  decodeId(encodedId) {
    return atob(encodedId);
  }

  // Convert flow data to chatbot format
  convertFlowToChatbot(flowData) {
    console.log('🔄 === CONVERTING FLOW TO CHATBOT FORMAT ===');
    console.log('📝 Input flow data:', flowData);
    
    // Extract trigger keywords from start nodes
    const triggerKeywords = [];
    const chatbotNodes = {};
    
    if (flowData.nodes && flowData.nodes.length > 0) {
      // Find start nodes (nodes with type 'start')
      const startNodes = flowData.nodes.filter(node => node.data.type === 'start');
      
      // Extract keywords from start nodes
      startNodes.forEach(startNode => {
        if (startNode.data.keywords && Array.isArray(startNode.data.keywords)) {
          triggerKeywords.push(...startNode.data.keywords);
        }
      });
      
      // Create the same flow sequence for each trigger keyword
      triggerKeywords.forEach(keyword => {
        chatbotNodes[keyword] = [];
        
        // Find the start node message
        const startNode = startNodes.find(node => 
          node.data.keywords && node.data.keywords.includes(keyword)
        );
        
        // Find the first message node connected to the start node
        const startNodeEdges = flowData.edges?.filter(edge => edge.source === startNode?.id) || [];
        const firstMessageNode = startNodeEdges.length > 0 ? 
          flowData.nodes.find(node => node.id === startNodeEdges[0].target) : null;
        
        // Find the second message node connected to the first message node
        const secondMessageNode = firstMessageNode ? 
          flowData.edges?.filter(edge => edge.source === firstMessageNode.id) : [];
        const secondNode = secondMessageNode.length > 0 ? 
          flowData.nodes.find(node => node.id === secondMessageNode[0].target) : null;
        
        // Create the first node (Start Bot Flow)
        chatbotNodes[keyword].push({
          node_id: 1,
          next_node_id: 2,
          type: 'message',
          message: startNode?.data.message || 'Start Bot Flow',
          delay: startNode?.data.delay || 0
        });
        
        // Create the second node (Hello! How can I help you today?)
        if (firstMessageNode) {
          chatbotNodes[keyword].push({
            node_id: 2,
            next_node_id: null,
            type: 'message',
            message: firstMessageNode.data.message || 'Hello! How can I help you today?',
            delay: firstMessageNode.data.delay || 0
          });
        }
        
        // If there's a third node, add it
        if (secondNode) {
          chatbotNodes[keyword][1].next_node_id = 3;
          chatbotNodes[keyword].push({
            node_id: 3,
            next_node_id: null,
            type: 'message',
            message: secondNode.data.message || '',
            delay: secondNode.data.delay || 0
          });
        }
      });
    }
    
    const chatbotData = {
      isactive: flowData.is_active ? 1 : 0,
      chatbot_name: flowData.name || 'Untitled Chatbot',
      tgm: triggerKeywords, // Array of trigger keywords only
      chatbot_node: chatbotNodes // Object with trigger keys mapping to arrays of node configurations
    };
    
    console.log('✅ Converted to chatbot format:', chatbotData);
    return chatbotData;
  }

  // Map node types to API format
  mapNodeType(type) {
    const typeMap = {
      'start': 'message',
      'message': 'message',
      'text_button': 'message',
      'interactive': 'interactive',
      'buttons': 'interactive',
      'media': 'media',
      'audio': 'audio',
      'file': 'file',
      'cta_url': 'cta',
      'ecommerce': 'ecommerce',
      'list_messages': 'list',
      'http_api': 'api',
      'condition': 'condition'
    };
    
    return typeMap[type] || 'message';
  }

  // Extract node data based on type
  extractNodeData(node) {
    const baseData = {
      delay: node.data.delay || 0
    };
    
    switch (node.data.type) {
      case 'start':
      case 'message':
      case 'text_button':
        return {
          ...baseData,
          message: node.data.message || ''
        };
        
      case 'interactive':
      case 'buttons':
        return {
          ...baseData,
          buttons: (node.data.buttons || []).map(button => ({
            message: button.label || button.message || ''
          }))
        };
        
      case 'media':
        return {
          ...baseData,
          message: node.data.message || '',
          media_url: node.data.mediaUrl || '',
          media_type: node.data.mediaType || 'image'
        };
        
      case 'audio':
        return {
          ...baseData,
          message: node.data.message || '',
          audio_url: node.data.audioUrl || ''
        };
        
      case 'file':
        return {
          ...baseData,
          message: node.data.message || '',
          file_url: node.data.fileUrl || '',
          file_name: node.data.fileName || ''
        };
        
      case 'cta_url':
        return {
          ...baseData,
          message: node.data.message || '',
          url: node.data.url || '',
          button_text: node.data.buttonText || 'Click Here'
        };
        
      case 'ecommerce':
        return {
          ...baseData,
          message: node.data.message || '',
          products: node.data.products || []
        };
        
      case 'list_messages':
        return {
          ...baseData,
          message: node.data.message || '',
          list_items: node.data.listItems || []
        };
        
      case 'http_api':
        return {
          ...baseData,
          message: node.data.message || '',
          api_url: node.data.apiUrl || '',
          method: node.data.method || 'GET'
        };
        
      case 'condition':
        return {
          ...baseData,
          condition: node.data.condition || '',
          true_node_id: node.data.trueNodeId || null,
          false_node_id: node.data.falseNodeId || null
        };
        
      default:
        return {
          ...baseData,
          message: node.data.message || ''
        };
    }
  }

  // Convert chatbot data to flow format
  convertChatbotToFlow(chatbotData) {
    console.log('🔄 === CONVERTING CHATBOT TO FLOW FORMAT ===');
    console.log('📝 Input chatbot data:', chatbotData);
    
    const nodes = [];
    const edges = [];
    let nodeCounter = 0;
    
    // Convert chatbot nodes back to flow nodes
    if (chatbotData.chatbot_node && typeof chatbotData.chatbot_node === 'object') {
      Object.entries(chatbotData.chatbot_node).forEach(([key, nodeConfigArray]) => {
        // Create a start node for this trigger keyword
        const startNodeId = `node_${nodeCounter++}`;
        const startNode = {
          id: startNodeId,
          type: 'custom',
          position: { x: nodeCounter * 200, y: nodeCounter * 100 },
          data: {
            type: 'start',
            message: `Bot triggered by "${key}"`,
            keywords: [key], // Use the key as the trigger keyword
            matchingType: 'exact',
            title: `Bot triggered by "${key}"`,
            addLabels: [],
            removeLabels: [],
            subscribeSequence: '',
            unsubscribeSequence: '',
            customField: '',
            assignToGroup: '',
            assignToUser: '',
            webhookUrl: '',
            rootNodeId: startNodeId,
            setNodeData: (updater) => {
              // This would need to be handled by the parent component
            },
            onDelete: () => {
              // This would need to be handled by the parent component
            },
            onCopy: () => {
              // This would need to be handled by the parent component
            }
          }
        };
        nodes.push(startNode);

        // Create nodes for each configuration in the array
        let previousNodeId = startNodeId;
        nodeConfigArray.forEach((nodeConfig, index) => {
          const nodeId = `node_${nodeCounter++}`;
          const node = {
            id: nodeId,
            type: 'custom',
            position: { x: nodeCounter * 200, y: (nodeCounter + index) * 100 },
            data: this.convertNodeConfigToFlowData(nodeConfig, key, chatbotData.tgm)
          };
          nodes.push(node);
          
          // Create edge from previous node to current node
          if (previousNodeId !== startNodeId || index === 0) {
            edges.push({
              id: `edge_${previousNodeId}_${nodeId}`,
              source: previousNodeId,
              target: nodeId,
              type: 'smoothstep'
            });
          }
          
          previousNodeId = nodeId;
        });
      });
    }
    
    const flowData = {
      id: chatbotData.id,
      name: chatbotData.chatbot_name,
      description: chatbotData.description || '',
      nodes: nodes,
      edges: edges,
      settings: {
        language: 'en',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      is_active: chatbotData.isactive === 1
    };
    
    console.log('✅ Converted to flow format:', flowData);
    return flowData;
  }

  // Convert node configuration back to flow data format
  convertNodeConfigToFlowData(nodeConfig, key, triggerKeywords) {
    const baseData = {
      delay: nodeConfig.delay || 0,
      setNodeData: () => {}, // Placeholder function
      onDelete: () => {}, // Placeholder function
      onCopy: () => {} // Placeholder function
    };
    
    switch (nodeConfig.type) {
      case 'message':
        // Check if this is a start node (if key matches trigger keywords)
        if (triggerKeywords && Array.isArray(triggerKeywords) && triggerKeywords.includes(key)) {
          return {
            ...baseData,
            type: 'start',
            message: nodeConfig.message || '',
            keywords: [key], // Use the key as the trigger keyword
            matchingType: 'exact',
            title: `Bot triggered by "${key}"`
          };
        } else {
          return {
            ...baseData,
            type: 'message',
            message: nodeConfig.message || ''
          };
        }
        
      case 'interactive':
        return {
          ...baseData,
          type: 'interactive',
          message: nodeConfig.message || '',
          buttons: (nodeConfig.buttons || []).map(button => ({
            label: button.message || '',
            action: 'reply',
            value: button.message || ''
          }))
        };
        
      case 'media':
        return {
          ...baseData,
          type: 'media',
          message: nodeConfig.message || '',
          mediaUrl: nodeConfig.media_url || '',
          mediaType: nodeConfig.media_type || 'image'
        };
        
      case 'audio':
        return {
          ...baseData,
          type: 'audio',
          message: nodeConfig.message || '',
          audioUrl: nodeConfig.audio_url || ''
        };
        
      case 'file':
        return {
          ...baseData,
          type: 'file',
          message: nodeConfig.message || '',
          fileUrl: nodeConfig.file_url || '',
          fileName: nodeConfig.file_name || ''
        };
        
      case 'cta':
        return {
          ...baseData,
          type: 'cta_url',
          message: nodeConfig.message || '',
          url: nodeConfig.url || '',
          buttonText: nodeConfig.button_text || 'Click Here'
        };
        
      case 'ecommerce':
        return {
          ...baseData,
          type: 'ecommerce',
          message: nodeConfig.message || '',
          products: nodeConfig.products || []
        };
        
      case 'list':
        return {
          ...baseData,
          type: 'list_messages',
          message: nodeConfig.message || '',
          listItems: nodeConfig.list_items || []
        };
        
      case 'api':
        return {
          ...baseData,
          type: 'http_api',
          message: nodeConfig.message || '',
          apiUrl: nodeConfig.api_url || '',
          method: nodeConfig.method || 'GET'
        };
        
      case 'condition':
        return {
          ...baseData,
          type: 'condition',
          condition: nodeConfig.condition || '',
          trueNodeId: nodeConfig.true_node_id || null,
          falseNodeId: nodeConfig.false_node_id || null
        };
        
      default:
        return {
          ...baseData,
          type: 'message',
          message: nodeConfig.message || ''
        };
    }
  }
}

export default new ChatbotAPI(); 